import { AfterViewInit, Component, ElementRef, LOCALE_ID, OnDestroy, OnInit, inject } from '@angular/core';
import { DOCUMENT, DecimalPipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { DomSanitizer, Meta, SafeResourceUrl, Title } from '@angular/platform-browser';
import { links } from '../../shared/links';
import { initFeatureFx } from '../feature-fx.util';

/**
 * Kommerzielle SEO-Landingpage „Bierautomat kaufen“ (/bierautomat).
 * Eigener Winkel: Alkohol RECHTSSICHER (Altersverifikation) und UNGEKÜHLT
 * verkaufen – Flaschen-/Dosenbier ist lagerfähig, ideal für den ungekühlten
 * Automaten. Referenz: Automatenkiosk Langenargen (Live-Link). Blog-Zubringer:
 * alkoholisches-in-automaten-verkaufen.
 */
@Component({
  selector: 'app-bierautomat-page',
  standalone: true,
  imports: [RouterModule, DecimalPipe],
  templateUrl: './bierautomat-page.component.html',
  styleUrls: ['../cluster-landing.shared.scss'],
})
export class BierautomatPageComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);
  private readonly locale = inject(LOCALE_ID);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly sanitizer = inject(DomSanitizer);
  private observers: IntersectionObserver[] = [];
  private revealFallback?: ReturnType<typeof setTimeout>;

  readonly links = links;
  readonly blogAlkohol = 'alkoholisches-in-automaten-verkaufen';

  /** Live-Beispiel: Automatenkiosk Langenargen. */
  readonly liveUrl: SafeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
    'https://live.marktbox.de/automatenkiosk-langenargen/U-MB-00082'
  );

  readonly priceBuyFrom = 6800;
  readonly priceRentFrom = 300;

  readonly faqs: { q: string; a: string }[] = [
    {
      q: $localize`:@@bier.faq1.q:Darf ich Bier über einen Automaten verkaufen?`,
      a: $localize`:@@bier.faq1.a:Ja – mit Altersverifikation. Die marktbox ist mit Altersverifikation erhältlich, sodass Alkohol jugendschutzkonform abgegeben wird. Details, worauf rechtlich zu achten ist, findest du in unserem Ratgeber.`,
    },
    {
      q: $localize`:@@bier.faq2.q:Muss Bier im Automaten gekühlt werden?`,
      a: $localize`:@@bier.faq2.a:Flaschen- und Dosenbier ist ungekühlt lagerfähig. Die marktbox ist ein ungekühlter Automat – für Bier zum Mitnehmen also genau richtig.`,
    },
    {
      q: $localize`:@@bier.faq3.q:Kann ich auch Sixpacks oder Bündel verkaufen?`,
      a: $localize`:@@bier.faq3.a:Ja. Die Fächer lassen sich flexibel belegen – von der Einzelflasche bis zum 6er-Träger oder Bündel. Gerade für Gebinde ist die marktbox besonders flexibel.`,
    },
    {
      q: $localize`:@@bier.faq4.q:Bierautomat kaufen oder mieten?`,
      a: $localize`:@@bier.faq4.a:Beides: ab 6.800 € kaufen oder ab 300 € pro Monat mieten. Mieten senkt die Einstiegshürde, Kaufen lohnt sich im Dauerbetrieb.`,
    },
  ];

  ngOnInit(): void {
    const canonical = this.canonicalUrl();
    const title = $localize`:@@bier.metaTitle:Bierautomat kaufen & mieten | marktbox`;
    const description =
      $localize`:@@bier.metaDesc:Bierautomat kaufen oder mieten: Flaschen- & Dosenbier 24/7 verkaufen – mit Altersverifikation, ungekühlt lagerfähig. Ab 6.800 € oder 300 €/Monat.`;

    this.title.setTitle(title);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({
      name: 'keywords',
      content: $localize`:@@bier.metaKeywords:Bierautomat, Bier verkaufen Automat, Bierautomat mit Altersverifikation, Getränke Direktvermarktung, marktbox`,
    });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: 'marktbox' });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:url', content: canonical });
    this.meta.updateTag({
      property: 'og:image',
      content: 'https://www.marktbox.de/assets/headers/marktbox-00-1920.webp',
    });

    this.setCanonical(canonical);
    this.injectJsonLd('bierautomat-product-schema', {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'marktbox Bierautomat',
      description,
      image: 'https://www.marktbox.de/assets/headers/marktbox-00-1920.webp',
      brand: { '@type': 'Brand', name: 'marktbox' },
      category: 'Verkaufsautomat',
      offers: [
        {
          '@type': 'Offer',
          name: 'Kauf',
          priceCurrency: 'EUR',
          price: this.priceBuyFrom,
          availability: 'https://schema.org/InStock',
          itemCondition: 'https://schema.org/NewCondition',
          url: canonical,
          seller: { '@type': 'Organization', name: 'marktbox' },
        },
        {
          '@type': 'Offer',
          name: 'Miete',
          priceCurrency: 'EUR',
          price: this.priceRentFrom,
          availability: 'https://schema.org/InStock',
          url: canonical,
          seller: { '@type': 'Organization', name: 'marktbox' },
        },
      ],
    });
    this.injectJsonLd('bierautomat-faq-schema', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: this.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }

  ngAfterViewInit(): void {
    this.observers = initFeatureFx(this.host.nativeElement);
    if (typeof window !== 'undefined' && typeof IntersectionObserver !== 'undefined') {
      this.revealFallback = setTimeout(() => {
        this.host.nativeElement
          .querySelectorAll('.reveal:not(.reveal-in)')
          .forEach((el) => el.classList.add('reveal-in'));
      }, 1200);
    }
  }

  ngOnDestroy(): void {
    this.observers.forEach((o) => o.disconnect());
    this.observers = [];
    if (this.revealFallback) clearTimeout(this.revealFallback);
  }

  scrollTo(event: Event, id: string): void {
    event.preventDefault();
    this.document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private canonicalUrl(): string {
    const localePrefix = this.locale.startsWith('da') ? 'da' : this.locale.startsWith('nl') ? 'nl' : 'de';
    return `https://marktbox.de/${localePrefix}/bierautomat`;
  }

  private setCanonical(href: string): void {
    let link = this.document.querySelector<HTMLLinkElement>('link[rel=canonical]');
    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'canonical';
      this.document.head.appendChild(link);
    }
    link.href = href;
  }

  private injectJsonLd(id: string, obj: object): void {
    this.document.getElementById(id)?.remove();
    const script = this.document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(obj);
    this.document.head.appendChild(script);
  }
}
